import { useState } from 'react'
import { apiClient } from '@/services/api.ts'
import { useErrorHandler } from '@/hooks/useAuth'

interface UploadProgress {
  [key: string]: number
}

interface PresignedUrlResponse {
  presigned_url: string
  file_key: string
  temp_key: string
  operation: string
  expires_at: string
  expires_in: number
  media_type: string
  session_id: string
}

interface UploadedImage {
  file: File
  preview: string
  name: string
  presignedUrl: string
  fileKey: string
  tempKey: string
  sessionId: string
}

interface UseUploadImageState {
  isLoading: boolean
  error: Nullable<string>
  uploadProgress: UploadProgress
}

interface UseUploadImageReturn extends UseUploadImageState {
  uploadImage: (file: File, resourceId: number) => Promise<UploadedImage>
  uploadImages: (files: File[]) => Promise<UploadedImage[]>
  reset: () => void
}

export function useUploadImage(): UseUploadImageReturn {
  const { handleError } = useErrorHandler()
  const [state, setState] = useState<UseUploadImageState>({
    isLoading: false,
    error: null,
    uploadProgress: {},
  })

  const uploadImage = async (
    file: File,
    resourceId: number
  ): Promise<UploadedImage> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    try {
      const presignedResponse = await apiClient.post('/media/presigned-url', {
        filename: file.name,
        resource_type: 'drawer',
        resource_id: resourceId,
        media_type: 'image',
        operation: 'PUT',
        expiration: 60,
        content_type: file.type,
        temp: false,
        session_id: 'c8aa1d92-9f7a-4c7e-9e9f-4b27b7c1f0a3',
      })

      const response: PresignedUrlResponse = presignedResponse.data

      await fetch(response.presigned_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      })

      const getPresignedResponse = await apiClient.post(
        '/media/presigned-urls/bulk',
        {
          urls: [
            {
              filename: file.name,
              resource_type: 'drawer',
              resource_id: resourceId,
              media_type: 'image',
              operation: 'GET',
              expiration: 3600,
              content_type: file.type,
              temp: false,
            },
          ],
        }
      )

      const getData: PresignedUrlResponse[] = getPresignedResponse.data.urls

      const reader = new FileReader()
      const preview = await new Promise<string>(resolve => {
        reader.onload = e => {
          resolve(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      })

      setState(prev => ({ ...prev, error: null }))

      return {
        file,
        preview,
        name: file.name,
        presignedUrl: getData[0].presigned_url,
        fileKey: response.file_key,
        tempKey: response.temp_key,
        sessionId: response.session_id,
      }
    } catch (error) {
      const errorMessage = handleError(error)
      setState(prev => ({ ...prev, error: errorMessage }))
      throw error
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }

  const uploadImages = async (files: File[]): Promise<UploadedImage[]> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    try {
      const bulkPresignedResponse = await apiClient.post(
        '/media/presigned-urls/bulk',
        {
          urls: files.map(file => ({
            filename: file.name,
            media_type: 'image',
            expiration: 3600,
            operation: 'PUT',
            resource_type: 'drawer',
            content_type: file.type,
            temp: false,
          })),
        }
      )

      const responseData = bulkPresignedResponse.data
      const putPresignedUrls: PresignedUrlResponse[] = responseData.urls

      await Promise.all(
        files.map((file, index) =>
          fetch(putPresignedUrls[index].presigned_url, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          })
        )
      )

      const getPresignedResponse = await apiClient.post(
        '/media/presigned-urls/bulk',
        {
          urls: files.map((file, index) => ({
            filename: file.name,
            file_key: responseData.urls[index].file_key,
            media_type: 'image',
            operation: 'GET',
            expiration: 3600,
          })),
        }
      )

      const getPresignedUrls: PresignedUrlResponse[] =
        getPresignedResponse.data.urls

      const uploadedImages: UploadedImage[] = await Promise.all(
        files.map(async (file, index) => {
          const reader = new FileReader()
          const preview = await new Promise<string>(resolve => {
            reader.onload = e => {
              resolve(e.target?.result as string)
            }
            reader.readAsDataURL(file)
          })

          setState(prev => ({
            ...prev,
            uploadProgress: {
              ...prev.uploadProgress,
              [file.name]: 100,
            },
          }))

          return {
            file,
            preview,
            name: file.name,
            presignedUrl: getPresignedUrls[index].presigned_url,
            fileKey: putPresignedUrls[index].file_key,
            tempKey: putPresignedUrls[index].temp_key,
            sessionId: putPresignedUrls[index].session_id,
          }
        })
      )

      setState(prev => ({ ...prev, error: null }))
      return uploadedImages
    } catch (error) {
      const errorMessage = handleError(error)
      setState(prev => ({ ...prev, error: errorMessage }))
      throw error
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }

  const reset = () => {
    setState({
      isLoading: false,
      error: null,
      uploadProgress: {},
    })
  }

  return {
    ...state,
    uploadImage,
    uploadImages,
    reset,
  }
}
